import { Injectable } from "@angular/core";
import { User } from "@app/models/user";
import { clearSalesforceUser, syncUserToSalesforce } from "@app/helpers/salesforce.helper";
import { sfLog } from "@app/core/salesforce/salesforce-debug.logger";
import { SalesforceMarketingCloudService } from "@app/services/salesforce-marketing-cloud.service";
import { SharedService } from "@app/services/shared.service";

/**
 * Mantém o contact key do Salesforce MC alinhado ao PV (clientId) do usuário autenticado.
 *
 * O MobilePush entrega diretamente por APNs/FCM.
 * O registro do device (token APNs + opt-in) é feito na camada nativa —
 * `ios/App/App/AppDelegate.swift` → `didRegisterForRemoteNotificationsWithDeviceToken`.
 * Aqui só cuidamos da identidade do contato: contact key, atributos e tags.
 */
@Injectable({
  providedIn: "root",
})
export class SalesforceUserSyncService {
  private syncInProgress = false;
  private lastSyncedClientId: string | null = null;

  constructor(
    private shared: SharedService,
    private salesforceService: SalesforceMarketingCloudService
  ) {
    this.shared.user$.subscribe((user) => {
      void this.handleUserChange(user);
    });
  }

  /**
   * Garante listener ativo e tenta sync do usuário já em memória (ex.: após init do SDK).
   */
  async syncCurrentUserIfAuthenticated(): Promise<void> {
    await this.handleUserChange(this.shared.user);
  }

  private async handleUserChange(user?: User): Promise<void> {
    if (!user) {
      if (this.lastSyncedClientId) {
        sfLog.info("Usuário deslogado — registrando logout no Salesforce");
        await clearSalesforceUser(this.salesforceService);
        this.lastSyncedClientId = null;
      }
      return;
    }

    const contactKey = this.resolveContactKey(user);
    if (!contactKey) {
      sfLog.warn("Sync ignorado: usuário autenticado sem PV (clientId)", {
        userStatus: user.userStatus,
        cpfCnpj: user.cpfCnpj ?? "(ausente)",
      });
      return;
    }

    if (!this.isUserAuthenticated()) {
      sfLog.warn("Sync ignorado: PV presente mas sem sessão autenticada (accessToken ausente)", {
        contactKey,
      });
      return;
    }

    if (this.syncInProgress) {
      sfLog.info("Sync já em andamento — aguardando conclusão", { contactKey });
      return;
    }

    if (contactKey === this.lastSyncedClientId) {
      return;
    }

    this.syncInProgress = true;
    try {
      const sdkReady = await this.ensureSdkReady();
      if (!sdkReady) {
        sfLog.error("Sync abortado: SDK Salesforce não ficou pronto a tempo", undefined, { contactKey });
        return;
      }

      sfLog.info("Iniciando sync do usuário no Salesforce", {
        contactKey,
        origem: "user$",
      });

      const synced = await syncUserToSalesforce(user, this.salesforceService);
      if (!synced) {
        sfLog.error("Sync do usuário falhou — contact key pode não estar no SFMC", undefined, { contactKey });
        return;
      }

      const confirmedKey = await this.salesforceService.getContactKey();
      if (confirmedKey !== contactKey) {
        sfLog.error("Contact key divergente após sync", undefined, {
          esperado: contactKey,
          atualNoSdk: confirmedKey ?? "(vazio)",
        });
        return;
      }

      this.lastSyncedClientId = contactKey;

      sfLog.ok("Usuário registrado no Salesforce com contact key = PV", {
        contactKey: confirmedKey,
      });
    } finally {
      this.syncInProgress = false;
    }
  }

  private resolveContactKey(user: User): string | null {
    const raw = user.clientId;
    if (raw === undefined || raw === null || raw === "") {
      return null;
    }
    return String(raw).trim();
  }

  private isUserAuthenticated(): boolean {
    return Boolean(this.shared.accessToken?.trim());
  }

  private async ensureSdkReady(maxAttempts = 8, delayMs = 500): Promise<boolean> {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (this.salesforceService.isReady()) {
        return true;
      }

      const initialized = await this.salesforceService.initialize();
      if (initialized) {
        return true;
      }

      if (attempt < maxAttempts) {
        sfLog.warn(`SDK Salesforce ainda não pronto — nova tentativa ${attempt}/${maxAttempts}`);
        await this.delay(delayMs);
      }
    }

    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
